/**
 * APRIL FOOLS 2026: Remove after April 1
 *
 * Matrix Rain Effect for Sidebar
 *
 * Renders a Matrix-style digital rain animation at the bottom of the sidebar.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { sidebarConfig, useSidebarOpenState } from '@backstage/core-components';
import { useApi, appThemeApiRef } from '@backstage/core-plugin-api';
import useObservable from 'react-use/lib/useObservable';
import { MatrixRainSurface } from '@internal/backstage-plugin-neo4j-ui-kit-react';

export function SidebarMatrixRain() {
  const appThemeApi = useApi(appThemeApiRef);
  const themeId = useObservable(
    appThemeApi.activeThemeId$(),
    appThemeApi.getActiveThemeId(),
  );

  const { isOpen } = useSidebarOpenState();
  const width = isOpen
    ? sidebarConfig.drawerWidthOpen
    : sidebarConfig.drawerWidthClosed;

  const containerRef = useRef<HTMLDivElement>(null);
  const [rainHeight, setRainHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 800,
  );

  useEffect(() => {
    if (themeId !== 'aura-matrix') {
      return undefined;
    }
    const el = containerRef.current;
    if (!el) {
      return undefined;
    }
    const update = () => {
      const h = el.getBoundingClientRect().height;
      setRainHeight(Math.max(1, Math.round(h)));
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [themeId, width]);

  const matrixElement = useMemo(
    () => (
      <MatrixRainSurface
        width={width}
        height={rainHeight}
        intensity="medium"
        fontSize={12}
        respectToggle={false}
      />
    ),
    [width, rainHeight],
  );

  if (themeId !== 'aura-matrix') {
    return null;
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        zIndex: 1,
        width,
        background:
          'linear-gradient(transparent 0%, rgba(0,10,0,0.15) 50%, rgba(0,10,0,0.25) 100%)',
        pointerEvents: 'none',
        opacity: 0.5,
      }}
    >
      {matrixElement}
    </div>
  );
}
